/**
 * Main App Component
 * WeHunt E-commerce Platform
 */

import { lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout, LayoutLoading, LayoutError } from "@/components/layout";
import { CartContext } from "@/contexts/CartContext";
import { useLayout } from "@/hooks/useLayout";
import { useLocaleOptions } from "@/hooks/useLocaleOptions";
import { useTranslations } from "@/hooks/useTranslations";
import { useContent } from "@/hooks/useContent";
import {
  LocalizedTextsContext,
  useLocalizedTexts,
  t,
} from "@/utils/translations";
import { BlockGrid } from "@/components/content/BlockGrid";
import { renderBlock } from "@/components/content/BlockRenderer";
import type { BlockGridLayout } from "@/types/content";
import { CartProvider } from "@/contexts/CartContext";
import { ProductProvider } from "@/contexts/ProductContext";
import { useContentByKey } from "@/hooks/useContentByKey";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useSsoSessionWatch } from "@/hooks/useSsoSessionWatch";
import { SeoHead } from "@/components/seo/SeoHead";
import { JsonLd } from "@/components/seo/JsonLd";
import type { JsonLdProductData } from "@/utils/json-ld";
import {
  AlternateUrlsContext,
  SetAlternateUrlsContext,
  type AlternateUrls,
} from "@/contexts/AlternateUrlsContext";
import { isProductPage, type ProductPageData } from "@/types/product";
import { getLocale, setLocale, hasStoredLocale } from "@/utils/locale-store";
import { storePreviewToken } from "@/utils/preview";
import { isGoPath } from "@/utils/crossAppMenu";
import { resolveEntry } from "@/utils/entryRedirect";
import { GoAccountRoute, GoCartRoute, GoShopRoute } from "@/routes/goRoutes";
import {
  extractLocaleFromPath,
  stripLocaleFromPath,
  getCountryFromCulture,
  detectBrowserLocale,
  resolveLocaleInfo,
  getSiteLocaleOverride,
  toUrlLocale,
} from "@/utils/locale-url";
import { localizeUrl, toRouteSlug } from "@/utils/url";
import { MyAccountPathContext } from "@/contexts/MyAccountPathContext";
import { IsUserAuthenticatedContext } from "@/contexts/IsUserAuthenticatedContext";
import { LocaleOptionsContext } from "@/contexts/LocaleOptionsContext";
import { buildLoginUrl, getGuestEmailFromUrl } from "@/utils/auth";
import type { LayoutPayload } from "@/types/layout";
import { B2bLoginPage } from "@/components/content/B2bLoginPage";
import { B2B_USER_TYPE } from "@/types/b2bAccount";
import { CartErrorBanner } from "@/components/layout/CartErrorBanner";
import { ImpersonationWidget } from "@/components/dev/ImpersonationWidget";
import { GoogleTagManager } from "@/components/analytics/GoogleTagManager";
import { AnalyticsIdentityProvider } from "@/components/analytics/AnalyticsIdentityProvider";
import {
  LoginTracker,
  PageViewTracker,
  ProductTracker,
} from "@/components/analytics/gtm-trackers";
import { getEnv } from "@/config/env";
import { isNotFoundError } from "@/api/http";
import { prefetchContent } from "@/api/content";
import { NotFoundPage } from "@/components/content/NotFoundPage";

// Pages most visits never open are loaded on demand: My account, the B2B
// account and checkout were over a third of the bundle every public page
// downloaded and parsed before it could render (LCP).
const MyAccountPage = lazy(() =>
  import("@/components/content/MyAccountPage").then((m) => ({
    default: m.MyAccountPage,
  }))
);
const B2bMyAccountPage = lazy(() =>
  import("@/components/content/B2bMyAccountPage").then((m) => ({
    default: m.B2bMyAccountPage,
  }))
);
const CheckoutPage = lazy(() =>
  import("@/components/checkout/CheckoutPage").then((m) => ({
    default: m.CheckoutPage,
  }))
);
const CheckoutReturn = lazy(() =>
  import("@/components/checkout/CheckoutReturn").then((m) => ({
    default: m.CheckoutReturn,
  }))
);
const CheckoutLoginChoice = lazy(() =>
  import("@/components/checkout/CheckoutLoginChoice").then((m) => ({
    default: m.CheckoutLoginChoice,
  }))
);
const B2bInvoiceCheckout = lazy(() =>
  import("@/components/checkout/B2bInvoiceCheckout").then((m) => ({
    default: m.B2bInvoiceCheckout,
  }))
);

/**
 * Shared content wrapper that fetches CMS content for the current path
 * (with locale prefix stripped) and renders loading/error states.
 */
function ContentPage({
  children,
}: {
  children: (
    content: NonNullable<ReturnType<typeof useContent>["content"]>
  ) => React.ReactNode;
}) {
  const { pathname, search } = useLocation();
  const contentPath = stripLocaleFromPath(pathname);
  const previewId = new URLSearchParams(search).get("previewId") ?? undefined;
  const { content, isLoading, error, redirectUrl, refetch } = useContent(
    contentPath,
    {
      previewId,
    }
  );
  const localizedTexts = useLocalizedTexts();
  const setAlternateUrls = useContext(SetAlternateUrlsContext);

  useEffect(() => {
    if (content?.alternateUrls) {
      setAlternateUrls(content.alternateUrls);
    } else {
      setAlternateUrls({});
    }
  }, [content, setAlternateUrls]);

  useEffect(() => {
    if (redirectUrl) {
      const localized = localizeUrl(redirectUrl, getLocale().language);
      window.location.assign(localized);
    }
  }, [redirectUrl]);

  if (isLoading || redirectUrl) {
    return <LayoutLoading />;
  }

  if (error && isNotFoundError(error)) {
    return <NotFoundPage />;
  }

  if (error) {
    const handleReset = () => {
      try {
        localStorage.removeItem("wehunt-locale");
      } catch {
        /* noop */
      }
      document.cookie.split(";").forEach((c) => {
        const name = c.split("=")[0]?.trim() ?? "";
        if (name.startsWith("umbracoCommerce")) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
      window.location.href = "/en/";
    };

    return (
      <div className="container" style={{ padding: "2rem 0" }}>
        <p style={{ color: "#c00" }}>Error loading content: {error}</p>
        <button
          type="button"
          onClick={refetch}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          {t(localizedTexts, "Tracker.Generic.TryAgain", "Try again")}
        </button>
        <button
          type="button"
          onClick={handleReset}
          style={{
            marginTop: "1rem",
            marginLeft: "0.5rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          Go to English homepage
        </button>
      </div>
    );
  }

  if (!content) return null;

  return <>{children(content)}</>;
}

/** Product page route -- fetches content, then fetches full product data by key */
function ProductPageRoute() {
  return (
    <ContentPage>
      {(content) => <ProductPageContent content={content} />}
    </ContentPage>
  );
}

/** Inner component that fetches full product data after content is loaded */
function ProductPageContent({
  content,
}: {
  content: NonNullable<ReturnType<typeof useContent>["content"]>;
}) {
  const {
    data: productData,
    isLoading,
    error,
    redirectUrl,
    refetch,
  } = useContentByKey<ProductPageData>(
    isProductPage(content) ? content.id : null
  );

  useEffect(() => {
    if (redirectUrl) {
      const localized = localizeUrl(redirectUrl, getLocale().language);
      window.location.assign(localized);
    }
  }, [redirectUrl]);

  // Build product price for SEO from full product data
  const productPrice = productData?.compositions?.product?.price
    ? {
        amount: String(productData.compositions.product.price.calculatedPrice),
        currency: productData.compositions.product.price.currency,
      }
    : null;

  // Build product JSON-LD data
  const jsonLdProductData: JsonLdProductData | undefined = productData
    ?.compositions?.product
    ? {
        name: productData.name,
        description: content.metaData?.metaDescription ?? undefined,
        image: productData.image?.url,
        sku: productData.compositions.product.sku,
        price: String(productData.compositions.product.price.calculatedPrice),
        currency: productData.compositions.product.price.currency,
        // The backend publishes a band, not a quantity. Only an explicit
        // "out of stock" band marks the offer unavailable — a null band means
        // stock is hidden or untracked, which is not a statement of absence.
        availability:
          productData.compositions.product.stockLevel === "outOfStock"
            ? "OutOfStock"
            : "InStock",
        brand: "WeHunt",
      }
    : undefined;

  const seo = useSeoMeta({
    metaData: content.metaData,
    openGraph: content.openGraph,
    twitterCard: content.twitterCard,
    pageType: content.type,
    alternateUrls: content.alternateUrls,
    productPrice,
  });

  if (isLoading || redirectUrl) {
    return <LayoutLoading />;
  }

  if (error && isNotFoundError(error)) {
    return <NotFoundPage />;
  }

  if (error) {
    return (
      <div className="container" style={{ padding: "2rem 0" }}>
        <p style={{ color: "#c00" }}>Error loading product data: {error}</p>
        <button
          type="button"
          onClick={refetch}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!productData || !content.blockGridLayout) {
    return null;
  }

  return (
    <ProductProvider productData={productData}>
      <SeoHead seo={seo} />
      <JsonLd
        composition={content.jsonLd}
        seo={seo}
        productData={jsonLdProductData}
      />
      <PageViewTracker content={content} />
      <ProductTracker product={productData} />
      <div className="pdp-page">
        <BlockGrid
          layout={content.blockGridLayout as BlockGridLayout}
          renderBlock={renderBlock}
        />
      </div>
    </ProductProvider>
  );
}

/** Generic CMS page route -- fetches content and renders block grid */
function CmsPage() {
  return (
    <ContentPage>
      {(content) =>
        // A product previewed from Umbraco arrives on the catch-all CMS route
        // (/{lang}/?preview&previewId=...), which has no products/:alias segment.
        // Render it through the product path so ProductProvider + product data
        // are present and the product blocks don't crash.
        isProductPage(content) ? (
          <ProductPageContent content={content} />
        ) : (
          <CmsPageContent content={content} />
        )
      }
    </ContentPage>
  );
}

/** Inner component for CMS pages — handles SEO and block grid rendering */
function CmsPageContent({
  content,
}: {
  content: NonNullable<ReturnType<typeof useContent>["content"]>;
}) {
  const seo = useSeoMeta({
    metaData: content.metaData,
    openGraph: content.openGraph,
    twitterCard: content.twitterCard,
    pageType: content.type,
    alternateUrls: content.alternateUrls,
    publishedTime: content.customPublishDate?.publishDate,
  });

  return (
    <>
      <SeoHead seo={seo} />
      <JsonLd composition={content.jsonLd} seo={seo} />
      <PageViewTracker content={content} />
      {content.blockGridLayout ? (
        <BlockGrid layout={content.blockGridLayout} renderBlock={renderBlock} />
      ) : (
        <div className="container" style={{ padding: "2rem 0" }}>
          <h1>WeHunt Webshop</h1>
          <p>Welcome to the WeHunt e-commerce platform.</p>
        </div>
      )}
    </>
  );
}

/**
 * Requires authentication (or guest email) before rendering checkout.
 * A signed-out visitor with a physical cart is offered guest checkout — by the
 * shop's own choice screen on SSO sites, by the identity server on legacy ones.
 * Digital subscription carts get no guest option, forcing a real sign-in.
 */
function AuthenticatedCheckout({
  layoutData,
  checkoutPath,
}: {
  layoutData: LayoutPayload;
  /** Locale-prefixed checkout URL (e.g. "/fi-FI/kassa"), derived from layout
   * data so the post-login return path matches the actual checkout route. */
  checkoutPath: string;
}): React.ReactElement | null {
  const guestEmail = getGuestEmailFromUrl();
  const cartCtx = useContext(CartContext);
  const cart = cartCtx?.cart ?? null;
  const cartLoading = cartCtx?.isLoading ?? true;

  // Wait for the cart so we know whether to allow guest.
  if (cartLoading) {
    return <LayoutLoading />;
  }

  // B2B members only pay by invoice — they never reach the Stripe checkout.
  if (layoutData.isUserAuthenticated && layoutData.userType === B2B_USER_TYPE) {
    return <B2bInvoiceCheckout layoutData={layoutData} />;
  }

  const isDigital =
    cart?.mode === "digitalOneTime" || cart?.mode === "digitalRecurring";

  // Authenticated members can always proceed; guests can only proceed for
  // physical-mode carts that have a forwarded guest email.
  if (layoutData.isUserAuthenticated || (guestEmail && !isDigital)) {
    return <CheckoutPage layoutData={layoutData} guestEmail={guestEmail} />;
  }

  // The SSO login page has no guest option, so on SSO sites the shop offers
  // the log-in-or-guest choice itself. Digital carts still need a member.
  if (!isDigital && getEnv().ssoLoginEnabled) {
    return (
      <CheckoutLoginChoice
        layoutData={layoutData}
        checkoutPath={checkoutPath}
      />
    );
  }

  // Redirect to the login page. On legacy sites the identity server offers the
  // guest option itself; it is disabled for digital carts.
  window.location.href = buildLoginUrl({
    returnPath: checkoutPath,
    allowGuest: !isDigital,
  });
  return null;
}

/**
 * Detects Umbraco preview URLs: /{contentId}?preview=true&lang=xx&c={ticket}
 * Returns { contentId, lang, token } if matched, null otherwise. `token` is the
 * backoffice ticket (`c`) the API needs to serve drafts; null when absent.
 */
function detectPreviewUrl(): {
  contentId: string;
  lang: string;
  token: string | null;
} | null {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("preview")) return null;

  const pathSegment = window.location.pathname.replace(/^\//, "");
  if (!/^\d+$/.test(pathSegment)) return null;

  return {
    contentId: pathSegment,
    lang: params.get("lang") || "en",
    token: params.get("c"),
  };
}

function App() {
  const { pathname } = useLocation();
  const preview = detectPreviewUrl();
  const urlLocale = extractLocaleFromPath(pathname);

  // The stable entry routes other apps link to (`/go/...`) carry no locale on
  // purpose: the caller cannot know the visitor's. They skip both locale
  // redirects below and resolve their localized destination themselves. A
  // first-time visitor still needs a locale to resolve against, so detect one
  // the same way the no-prefix branch does.
  const isGoRoute = isGoPath(pathname);
  if (isGoRoute && !hasStoredLocale()) {
    setLocale(detectBrowserLocale());
  }

  // Sync locale from URL to localStorage SYNCHRONOUSLY so that API calls
  // made by child components (useLayout, useContent) send correct headers.
  // This must happen before AppContent renders — a useEffect would be too late.
  // Also re-sync when the site enforces a country/currency for this locale
  // (e.g. wehuntno: en → NO/NOK) and the stored values predate that rule —
  // otherwise returning visitors would keep sending the old currency forever.
  // A /go/ route carries no URL locale, so it falls back to the just-resolved
  // stored one — otherwise a returning /go/ visitor whose stored locale
  // predates a site rule would never pick up the override above.
  const currentLocale = getLocale();
  const localeForSync =
    urlLocale ?? (isGoRoute ? currentLocale.language : null);
  const siteOverride = localeForSync
    ? getSiteLocaleOverride(localeForSync)
    : null;
  const overrideOutOfSync =
    siteOverride !== null &&
    (currentLocale.country !== siteOverride.country ||
      currentLocale.currency !== siteOverride.currency);
  if (
    localeForSync &&
    (localeForSync !== currentLocale.language || overrideOutOfSync)
  ) {
    const localeInfo = resolveLocaleInfo(localeForSync);
    const country = localeInfo?.country ?? getCountryFromCulture(localeForSync);
    const currency = localeInfo?.currency;
    setLocale({
      language: localeForSync,
      country,
      ...(currency && { currency }),
    });
  }

  useEffect(() => {
    if (urlLocale) {
      document.documentElement.lang = urlLocale;
    }
  }, [urlLocale]);

  // Intercept Umbraco preview URLs (/{contentId}?preview=true&lang=xx&c={ticket})
  // Redirect to /{lang}/?preview&previewId={contentId} so the content hook fetches by ID.
  // The ticket is kept for the tab (not carried on in the URL) and forwarded to the API
  // as X-Preview-Token by every request made in preview mode; without it the API has no
  // proof of an editor session and answers with published content. Idempotent write, so
  // doing it during render ahead of the Navigate is safe.
  if (preview) {
    if (preview.token) storePreviewToken(preview.token);
    return (
      <Navigate
        to={`/${toUrlLocale(preview.lang)}/?preview&previewId=${preview.contentId}`}
        replace
      />
    );
  }

  // If no locale prefix in URL, redirect to the best locale:
  // - Returning visitor → use their stored preference
  // - First visit → detect from browser Accept-Language
  // (Skipped entirely for a locale-prefixed or /go/ path — resolveEntry
  // would return redirectTo: null for both anyway, but this also avoids the
  // localStorage / navigator reads on every render of the common case.)
  if (!urlLocale && !isGoRoute) {
    const storedLocaleExists = hasStoredLocale();
    const detected = detectBrowserLocale();
    const entry = resolveEntry(
      pathname,
      window.location.search,
      storedLocaleExists,
      getLocale().language,
      detected.language
    );
    if (entry.redirectTo) {
      if (!storedLocaleExists) setLocale(detected);
      return <Navigate to={entry.redirectTo} replace />;
    }
  }

  // Redirect to the lower-case locale URL (e.g. /nb-NO/... → /nb-no/...).
  // nginx already 301s these on a full page load; this covers client-side
  // navigation to a hand-written or stale upper-case link.
  const rawFirstSegment = pathname.split("/").filter(Boolean)[0];
  if (
    !isGoRoute &&
    urlLocale &&
    rawFirstSegment &&
    rawFirstSegment !== toUrlLocale(urlLocale)
  ) {
    const canonicalPath = pathname.replace(
      `/${rawFirstSegment}`,
      `/${toUrlLocale(urlLocale)}`
    );
    return <Navigate to={canonicalPath + window.location.search} replace />;
  }

  // Key by locale so a client-side locale change (browser back/forward across a
  // switch, alternate-locale links) remounts AppContent and refetches all
  // locale-dependent data (layout, translations, locale options). Otherwise
  // those once-on-mount fetches keep the previous locale's page slugs, e.g. the
  // header account link renders /sv-SE/my-account instead of /sv-SE/mitt-konto.
  return <AppContent key={urlLocale ?? getLocale().language} />;
}

/** Inner component that loads layout/translations and renders routes */
function AppContent() {
  const {
    layoutData,
    isLoading: layoutLoading,
    error: layoutError,
    refetch: refetchLayout,
  } = useLayout();

  const {
    localeOptions,
    isLoading: localeOptionsLoading,
    error: localeOptionsError,
    refetch: refetchLocaleOptions,
  } = useLocaleOptions();

  const { translations } = useTranslations();
  const [alternateUrls, setAlternateUrls] = useState<AlternateUrls>({});

  // Before the early returns below, because hooks cannot be called conditionally.
  // Anonymous visitors have no SSO session, so the watch stays idle for them.
  useSsoSessionWatch({ enabled: layoutData?.isUserAuthenticated === true });

  // Request the landing page's content alongside the layout: the routes that
  // render it only exist once the layout is in. Only for the page this mount
  // starts on — later navigations find the layout already loaded.
  const { pathname, search } = useLocation();
  const hasPrefetchedRef = useRef(false);
  useEffect(() => {
    if (hasPrefetchedRef.current) return;
    hasPrefetchedRef.current = true;
    if (isGoPath(pathname)) return;
    prefetchContent(stripLocaleFromPath(pathname), {
      previewId: new URLSearchParams(search).get("previewId") ?? undefined,
    });
  }, [pathname, search]);

  if (layoutLoading || localeOptionsLoading) {
    return <LayoutLoading />;
  }

  if (layoutError || localeOptionsError || !layoutData || !localeOptions) {
    return (
      <LayoutError
        error={
          layoutError ??
          localeOptionsError ??
          t(
            translations,
            "Tracker.Generic.Error.FailedToLoadLayout",
            "Failed to load layout"
          )
        }
        onRetry={() => {
          refetchLayout();
          refetchLocaleOptions();
        }}
      />
    );
  }

  const locale = getLocale();

  // Editor-controlled page links come back as "#" when unset, so treat that the
  // same as missing rather than localizing the placeholder.
  const pageUrlOrFallback = (url: string | undefined, fallback: string) =>
    url && url !== "#" ? url : fallback;

  const rawCartPath = pageUrlOrFallback(
    layoutData.pages.cartPage?.url,
    "/cart"
  );
  const cartPath = localizeUrl(rawCartPath, locale.language);
  const rawCheckoutPath = pageUrlOrFallback(
    layoutData.pages.checkoutPage?.url,
    "/checkout"
  );
  const checkoutPath = localizeUrl(rawCheckoutPath, locale.language);
  // The checkout slug is localized and editor-controlled (e.g. "kassa" in
  // Finnish), so the route segment is derived from layout data rather than
  // hardcoding "checkout" — otherwise localized checkout URLs fall through to
  // the CMS catch-all and render the empty placeholder page.
  const checkoutSlug = toRouteSlug(rawCheckoutPath, "checkout");

  // The My Account slug is localized and editor-controlled (e.g. "oma-tili" in
  // Finnish), so we derive both the router slug and the locale-prefixed link
  // from layout data rather than hardcoding "my-account".
  const rawMyAccountPath =
    layoutData.pages.b2cMyAccountPage?.url || "/my-account";
  const myAccountSlug = toRouteSlug(rawMyAccountPath, "my-account");
  const myAccountPath = localizeUrl(rawMyAccountPath, locale.language);

  // The products container slug is localized and editor-controlled (e.g.
  // "tuotteet" in Finnish), so the product route segment is derived from layout
  // data rather than hardcoding "products" — otherwise localized product URLs
  // fall through to the CMS catch-all and render without a ProductProvider.
  const productsSlug = toRouteSlug(
    pageUrlOrFallback(layoutData.pages.productsListing?.url, "/products"),
    "products"
  );

  const rawB2bLoginPath = pageUrlOrFallback(
    layoutData.pages.b2bLoginPage?.url,
    "/b2b-login"
  );
  const b2bLoginSlug = toRouteSlug(rawB2bLoginPath, "b2b-login");
  const b2bLoginPath = localizeUrl(rawB2bLoginPath, locale.language);
  const rawB2bMyAccountPath = pageUrlOrFallback(
    layoutData.pages.b2bMyAccountPage?.url,
    "/b2b-account"
  );
  const b2bMyAccountSlug = toRouteSlug(rawB2bMyAccountPath, "b2b-account");
  const b2bMyAccountPath = localizeUrl(rawB2bMyAccountPath, locale.language);

  const { gtmId } = getEnv();

  return (
    <LocalizedTextsContext.Provider value={translations}>
      <LocaleOptionsContext.Provider value={localeOptions}>
        <AnalyticsIdentityProvider
          userType={layoutData.userType}
          fullId={layoutData.fullId}
          b2bUsername={layoutData.b2bUsername}
        >
          <GoogleTagManager gtmId={gtmId} />
          <LoginTracker isUserAuthenticated={layoutData.isUserAuthenticated} />
          <AlternateUrlsContext.Provider value={alternateUrls}>
            <SetAlternateUrlsContext.Provider value={setAlternateUrls}>
              <CartProvider cartUrl={cartPath} checkoutUrl={checkoutPath}>
                <CartErrorBanner />
                {/* Dev/test-only floating panel; renders nothing unless
                ENABLE_IMPERSONATION is set in env-config.json. */}
                <ImpersonationWidget
                  layoutData={layoutData}
                  myAccountPath={myAccountPath}
                  b2bMyAccountPath={b2bMyAccountPath}
                />
                <MyAccountPathContext.Provider value={myAccountPath}>
                  <IsUserAuthenticatedContext.Provider
                    value={layoutData.isUserAuthenticated}
                  >
                    {/* Catches the checkout pages, which have no Layout; the
                    Layout routes have their own boundary so the header and
                    footer stay up while a page loads. */}
                    <Suspense fallback={<LayoutLoading />}>
                      <Routes>
                        {/* Stable entry points for other apps; see src/routes/goRoutes.tsx. */}
                        <Route path="/go/shop" element={<GoShopRoute />} />
                        <Route
                          path="/go/cart"
                          element={<GoCartRoute cartPath={cartPath} />}
                        />
                        <Route
                          path="/go/account/:tab?"
                          element={
                            <GoAccountRoute
                              layoutData={layoutData}
                              myAccountPath={myAccountPath}
                              b2bMyAccountPath={b2bMyAccountPath}
                            />
                          }
                        />
                        {/* Any other /go/... path (a typo, a route retired on the
                    other end) lands on the storefront root rather than
                    falling through to the CMS catch-all below. */}
                        <Route path="/go/*" element={<GoShopRoute />} />

                        {/* Checkout page requires authentication and has its own layout */}
                        <Route
                          path={`/:locale/${checkoutSlug}`}
                          element={
                            <AuthenticatedCheckout
                              layoutData={layoutData}
                              checkoutPath={checkoutPath}
                            />
                          }
                        />

                        {/* Checkout return page (after Stripe payment redirect) */}
                        <Route
                          path={`/:locale/${checkoutSlug}/return`}
                          element={<CheckoutReturn layoutData={layoutData} />}
                        />

                        {/* All other locale-prefixed routes use the standard layout */}
                        <Route
                          path="/:locale/*"
                          element={
                            <Layout
                              layoutData={layoutData}
                              localeOptions={localeOptions}
                            >
                              <Suspense fallback={<LayoutLoading />}>
                                <Routes>
                                  <Route
                                    path={`${productsSlug}/:alias`}
                                    element={<ProductPageRoute />}
                                  />
                                  <Route
                                    path={myAccountSlug}
                                    element={
                                      <MyAccountPage layoutData={layoutData} />
                                    }
                                  />
                                  <Route
                                    path={b2bLoginSlug}
                                    element={
                                      <B2bLoginPage
                                        layoutData={layoutData}
                                        b2bMyAccountPath={b2bMyAccountPath}
                                      />
                                    }
                                  />
                                  <Route
                                    path={b2bMyAccountSlug}
                                    element={
                                      <B2bMyAccountPage
                                        layoutData={layoutData}
                                        b2bLoginPath={b2bLoginPath}
                                      />
                                    }
                                  />
                                  <Route path="*" element={<CmsPage />} />
                                </Routes>
                              </Suspense>
                            </Layout>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </IsUserAuthenticatedContext.Provider>
                </MyAccountPathContext.Provider>
              </CartProvider>
            </SetAlternateUrlsContext.Provider>
          </AlternateUrlsContext.Provider>
        </AnalyticsIdentityProvider>
      </LocaleOptionsContext.Provider>
    </LocalizedTextsContext.Provider>
  );
}

export default App;
